import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Plus, Target } from 'lucide-react';
import {
  listarOKRs,
  criarOKR,
  adicionarKeyResult,
  atualizarKeyResult,
  excluirOKR,
  type OKR,
  type KeyResult,
} from '@/features/okrs/okrs.api';
import { cn } from '@/lib/cn';

function progresso(kr: Pick<KeyResult, 'currentValue' | 'targetValue'>) {
  const cur = parseFloat(kr.currentValue);
  const tgt = parseFloat(kr.targetValue);
  return tgt > 0 ? Math.min(100, Math.round((cur / tgt) * 100)) : 0;
}

function okrProgresso(okr: OKR) {
  if (!okr.keyResults.length) return 0;
  return Math.round(
    okr.keyResults.reduce((s, kr) => s + progresso(kr), 0) / okr.keyResults.length,
  );
}

function fmtEpoch(ep: number) {
  return new Date(ep * 60 * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const FORM_VAZIO = { title: '', description: '', periodStart: '', periodEnd: '' };
type KrFormEntry = { title: string; target: string; unit: string };

export function OKRsPage() {
  const qc = useQueryClient();
  const { data = [], isPending } = useQuery({ queryKey: ['okrs'], queryFn: listarOKRs });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [criando, setCriando] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [krForm, setKrForm] = useState<Record<string, KrFormEntry>>({});
  const [editingKr, setEditingKr] = useState<Record<string, string>>({});

  const criarMut = useMutation({
    mutationFn: () =>
      criarOKR({
        title: form.title,
        description: form.description || undefined,
        periodStart: Math.floor(new Date(form.periodStart).getTime() / 60_000),
        periodEnd: Math.floor(new Date(form.periodEnd).getTime() / 60_000),
      }),
    onSuccess: (okr) => {
      void qc.invalidateQueries({ queryKey: ['okrs'] });
      setExpanded((prev) => new Set([...prev, okr.id]));
      setCriando(false);
      setForm(FORM_VAZIO);
    },
  });

  const addKrMut = useMutation({
    mutationFn: ({ okrId }: { okrId: string }) => {
      const f: KrFormEntry = krForm[okrId] ?? { title: '', target: '', unit: '' };
      return adicionarKeyResult(okrId, {
        title: f.title,
        targetValue: parseFloat(f.target),
        unit: f.unit || undefined,
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['okrs'] }),
  });

  const updateKrMut = useMutation({
    mutationFn: ({ okrId, krId, val }: { okrId: string; krId: string; val: number }) =>
      atualizarKeyResult(okrId, krId, val),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['okrs'] }),
  });

  const excluirMut = useMutation({
    mutationFn: (id: string) => excluirOKR(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['okrs'] }),
  });

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div className="mx-auto max-w-[900px] space-y-4">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">OKRs</h1>
        <button
          onClick={() => setCriando(true)}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-3.5 w-3.5" /> Novo OKR
        </button>
      </header>

      {isPending ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>
      ) : data.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12">
          <Target className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">Nenhum OKR criado ainda.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((okr) => {
            const pct = okrProgresso(okr);
            const open = expanded.has(okr.id);
            const kf: KrFormEntry = krForm[okr.id] ?? { title: '', target: '', unit: '' };
            return (
              <div
                key={okr.id}
                className="rounded-xl border border-border bg-background overflow-hidden"
              >
                {/* Cabeçalho do OKR */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-muted/20"
                  onClick={() => toggleExpanded(okr.id)}
                >
                  {open ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{okr.title}</p>
                    {okr.description && (
                      <p className="text-xs text-muted-foreground truncate">{okr.description}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      {fmtEpoch(okr.periodStart)} → {fmtEpoch(okr.periodEnd)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            pct === 100 ? 'bg-green-500' : 'bg-primary',
                          )}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium tabular-nums w-8 text-right">
                        {pct}%
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Excluir "${okr.title}"?`))
                          excluirMut.mutate(okr.id);
                      }}
                      className="text-[10px] text-muted-foreground hover:text-destructive px-1"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Resultados-chave */}
                {open && (
                  <div className="border-t border-border divide-y divide-border/50">
                    {okr.keyResults.map((kr) => {
                      const kpct = progresso(kr);
                      const editing = editingKr[kr.id];
                      return (
                        <div
                          key={kr.id}
                          className="flex items-center gap-3 px-6 py-2.5 text-sm bg-muted/10"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{kr.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="w-32 h-1.5 overflow-hidden rounded-full bg-muted">
                                <div
                                  className={cn(
                                    'h-full rounded-full',
                                    kpct === 100 ? 'bg-green-500' : 'bg-primary',
                                  )}
                                  style={{ width: `${kpct}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-muted-foreground tabular-nums">
                                {parseFloat(kr.currentValue)}
                                {kr.unit ? ` ${kr.unit}` : ''} /{' '}
                                {parseFloat(kr.targetValue)}
                                {kr.unit ? ` ${kr.unit}` : ''} ({kpct}%)
                              </span>
                            </div>
                          </div>
                          {editing !== undefined ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={editing}
                                onChange={(e) =>
                                  setEditingKr((p) => ({ ...p, [kr.id]: e.target.value }))
                                }
                                className="w-16 rounded border border-input/80 bg-background px-2 py-0.5 text-xs tabular-nums"
                              />
                              <button
                                onClick={() => {
                                  updateKrMut.mutate({
                                    okrId: okr.id,
                                    krId: kr.id,
                                    val: parseFloat(editing),
                                  });
                                  setEditingKr((p) => {
                                    const n = { ...p };
                                    delete n[kr.id];
                                    return n;
                                  });
                                }}
                                className="rounded bg-primary px-2 py-0.5 text-[10px] text-primary-foreground"
                              >
                                OK
                              </button>
                              <button
                                onClick={() =>
                                  setEditingKr((p) => {
                                    const n = { ...p };
                                    delete n[kr.id];
                                    return n;
                                  })
                                }
                                className="text-[10px] text-muted-foreground hover:underline"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() =>
                                setEditingKr((p) => ({ ...p, [kr.id]: kr.currentValue }))
                              }
                              className="text-[10px] text-muted-foreground hover:text-primary"
                            >
                              Atualizar
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {/* Formulário para novo KR */}
                    <div className="flex items-center gap-2 px-6 py-2 bg-muted/5">
                      <input
                        placeholder="Novo resultado-chave…"
                        value={kf.title}
                        onChange={(e) =>
                          setKrForm((p) => ({
                            ...p,
                            [okr.id]: { ...kf, title: e.target.value },
                          }))
                        }
                        className="flex-1 rounded-lg border border-input/80 bg-background px-2 py-1 text-xs"
                      />
                      <input
                        type="number"
                        placeholder="Meta"
                        value={kf.target}
                        onChange={(e) =>
                          setKrForm((p) => ({
                            ...p,
                            [okr.id]: { ...kf, target: e.target.value },
                          }))
                        }
                        className="w-16 rounded-lg border border-input/80 bg-background px-2 py-1 text-xs tabular-nums"
                      />
                      <input
                        placeholder="Unidade"
                        value={kf.unit}
                        onChange={(e) =>
                          setKrForm((p) => ({
                            ...p,
                            [okr.id]: { ...kf, unit: e.target.value },
                          }))
                        }
                        className="w-16 rounded-lg border border-input/80 bg-background px-2 py-1 text-xs"
                      />
                      <button
                        onClick={() => {
                          if (kf.title && kf.target) addKrMut.mutate({ okrId: okr.id });
                        }}
                        disabled={!kf.title || !kf.target}
                        className="rounded-lg bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary hover:bg-primary/20 disabled:opacity-40"
                      >
                        + KR
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: novo OKR */}
      {criando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border border-border bg-background p-6 space-y-4 shadow-2xl">
            <h2 className="text-base font-semibold">Novo OKR</h2>
            <div className="space-y-3">
              {(
                [
                  {
                    label: 'Objetivo',
                    key: 'title',
                    type: 'text',
                    placeholder: 'Ex.: Expandir a base de clientes',
                  },
                  {
                    label: 'Descrição (opcional)',
                    key: 'description',
                    type: 'text',
                    placeholder: '',
                  },
                  { label: 'Início do período', key: 'periodStart', type: 'date', placeholder: '' },
                  { label: 'Fim do período', key: 'periodEnd', type: 'date', placeholder: '' },
                ] as const
              ).map(({ label, key, type, placeholder }) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs text-muted-foreground">{label}</label>
                  <input
                    type={type}
                    value={form[key]}
                    placeholder={placeholder}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-input/80 bg-muted/40 px-3 text-sm focus-visible:outline-none focus-visible:border-ring/40"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCriando(false)}
                className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={() => criarMut.mutate()}
                disabled={
                  !form.title || !form.periodStart || !form.periodEnd || criarMut.isPending
                }
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
              >
                {criarMut.isPending ? 'Criando…' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
